import { ApiError } from '@fc/core'
import { Badge, Button, Card, Spinner } from '@fc/ui'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Check, Copy, Sparkles, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { PageHeader } from '../../components/page-header'
import { generateDraft, getAiStatus, type AiGenerateResult, type AiKind } from '../../lib/ai-api'

// Mirrors @fc/core AI_KIND_DEFS / size caps; kept inline so the page is
// self-contained and avoids importing constants that may not yet be re-exported.
const KINDS: { key: AiKind; label: string; description: string }[] = [
  { key: 'content', label: 'Content', description: 'Announcements, articles, sermon summaries.' },
  { key: 'page', label: 'Page', description: 'A public web page section (hero, about, blurb).' },
  { key: 'workflow', label: 'Workflow', description: 'Trigger, conditions, and follow-up steps.' },
  { key: 'form', label: 'Form', description: 'Proposed fields and labels for a form.' },
]

const PROMPT_MAX = 4000

export function AiPlayground() {
  const [kind, setKind] = useState<AiKind>('content')
  const [prompt, setPrompt] = useState('')
  const [draft, setDraft] = useState<AiGenerateResult | null>(null)
  const [copied, setCopied] = useState(false)

  const status = useQuery({ queryKey: ['ai', 'status'], queryFn: getAiStatus })

  const generate = useMutation({
    mutationFn: () => generateDraft({ kind, prompt: prompt.trim() }),
    onSuccess: (result) => {
      setDraft(result)
      setCopied(false)
    },
  })

  const disabled = status.data?.enabled === false
  const tooLong = prompt.length > PROMPT_MAX
  const canSubmit = prompt.trim().length > 0 && !tooLong && !disabled && !generate.isPending

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (canSubmit) generate.mutate()
  }

  const onCopy = async () => {
    if (!draft) return
    await navigator.clipboard.writeText(draft.draft)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const errorMessage = (() => {
    if (!generate.isError) return null
    const err = generate.error
    if (err instanceof ApiError) {
      if (err.code === 'ai_unconfigured') return 'AI assist is not configured on this server.'
      if (err.code === 'rate_limited')
        return 'Too many requests. Please wait a moment and try again.'
      if (err.code === 'forbidden') return 'You do not have permission to use AI assist.'
      return err.message
    }
    return err instanceof Error ? err.message : 'Something went wrong.'
  })()

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI assist"
        description="Draft content, pages, workflows, and forms with server-side Claude."
        actions={
          status.data?.enabled && status.data.model ? (
            <Badge variant="secondary" className="font-mono text-xs">
              {status.data.model}
            </Badge>
          ) : undefined
        }
      />

      {disabled && (
        <Card className="flex items-start gap-3 border-warning/40 bg-warning/5 p-4">
          <TriangleAlert className="mt-0.5 size-5 shrink-0 text-warning" />
          <div className="space-y-1 text-sm">
            <p className="font-medium text-fg">AI assist is disabled</p>
            <p className="text-muted">
              No AI provider is configured on this server. Set the <code>ANTHROPIC_API_KEY</code>{' '}
              environment variable on the API to enable drafting.
            </p>
          </div>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* ------------------------------------------------ prompt form */}
        <Card className="p-5">
          <form onSubmit={onSubmit} className="space-y-4">
            <fieldset className="space-y-2" disabled={disabled}>
              <legend className="text-sm font-medium text-fg">What do you want to draft?</legend>
              <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Draft kind">
                {KINDS.map((k) => {
                  const active = kind === k.key
                  return (
                    <button
                      key={k.key}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setKind(k.key)}
                      className={`rounded-lg border p-3 text-left transition-colors ${
                        active
                          ? 'border-secondary bg-card-hover'
                          : 'border-border bg-surface hover:bg-card-hover'
                      }`}
                    >
                      <span className="block text-sm font-medium text-fg">{k.label}</span>
                      <span className="mt-0.5 block text-xs text-dim">{k.description}</span>
                    </button>
                  )
                })}
              </div>
            </fieldset>

            <div className="space-y-1.5">
              <label htmlFor="ai-prompt" className="text-sm font-medium text-fg">
                Prompt
              </label>
              <textarea
                id="ai-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                disabled={disabled}
                rows={8}
                placeholder="Describe what you'd like Claude to draft…"
                aria-invalid={tooLong}
                className="flex w-full rounded-md border border-border bg-bg-alt px-3 py-2 text-sm text-fg placeholder:text-dim shadow-sm transition-colors focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-danger aria-[invalid=true]:ring-danger/40"
              />
              <div className="flex items-center justify-between text-xs">
                <span className="text-dim">
                  Only your prompt and authorized context are sent to the model.
                </span>
                <span className={tooLong ? 'text-danger' : 'text-dim'}>
                  {prompt.length}/{PROMPT_MAX}
                </span>
              </div>
            </div>

            {errorMessage && (
              <p className="flex items-center gap-1.5 text-sm text-danger" role="alert">
                <TriangleAlert className="size-4 shrink-0" />
                {errorMessage}
              </p>
            )}

            <div className="flex justify-end">
              <Button type="submit" disabled={!canSubmit}>
                {generate.isPending ? <Spinner /> : <Sparkles />}
                {generate.isPending ? 'Drafting…' : 'Generate draft'}
              </Button>
            </div>
          </form>
        </Card>

        {/* ------------------------------------------------ draft output */}
        <Card className="flex flex-col p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium text-fg">Draft</h2>
            {draft && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onCopy}
                aria-label="Copy draft to clipboard"
              >
                {copied ? <Check className="text-success" /> : <Copy />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            )}
          </div>

          <div className="mt-3 flex-1">
            {generate.isPending ? (
              <div className="flex h-full min-h-48 items-center justify-center gap-2 text-muted">
                <Spinner /> Generating…
              </div>
            ) : draft ? (
              <div className="space-y-3">
                <pre className="whitespace-pre-wrap break-words rounded-lg bg-bg-alt p-4 font-sans text-sm text-fg">
                  {draft.draft}
                </pre>
                <p className="text-xs text-dim">
                  {draft.usage.inputTokens} in · {draft.usage.outputTokens} out tokens ·{' '}
                  {draft.model}
                </p>
              </div>
            ) : (
              <div className="flex h-full min-h-48 items-center justify-center text-center text-sm text-muted">
                Your generated draft will appear here.
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}
